import React from 'react';
import { WeezHeader, Tab, Button, Icon, ToggleCard, Toggle, Card, Chip, Tooltip, ToastContext } from '@weezevent/nacre';
import DesignList from '../list/components/designList';
import TicketDesignEmptyCard from '../list/components/design-row/ticketDesignEmptyCard';
import useAppContext from '../../../context/useAppContext';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DesignModel from '../../../models/designModel';
import Loading from '../../../components/loading';
import { usePermissions } from '../../../user/permissions';
import FeatureFlagsModel from '../../../models/featureFlagsModel';

import Style from './designEvent.module.css';

export function SwitchFeature({ only_admin = true, event = null, organization, name }) {
    const permissions = usePermissions();
    const toast = React.useContext(ToastContext);
    const [{ feature_flags }] = useAppContext();
    const [loadingToggle, setLoadingToggle] = React.useState(false);
    const orgaActive = React.useMemo(() => organization.feature_flags.includes(name), [organization]);
    const isDisabled = React.useMemo(() => event && orgaActive, [event, orgaActive]);

    const defaultPremium = React.useMemo(() => feature_flags.includes(name), [feature_flags]);

    const [,] = FeatureFlagsModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event?.id,
            launch: Boolean(organization && !isDisabled),
            onSuccess: _res => {
                const toggled = _res?.find(f => f.key === name)?.value === '1';
                setToggleState(toggled);
            }
        },
        [organization, event, isDisabled]
    );

    const [toggleState, setToggleState] = React.useState(defaultPremium);

    const { t } = useTranslation();

    const onSetFeatureFlag = React.useCallback(() => {
        if (loadingToggle || isDisabled) return;
        const activated = !toggleState;
        setToggleState(activated);

        setLoadingToggle(true);
        const featureToSave = new FeatureFlagsModel({
            key: name,
            value: activated ? '1' : '0'
        });
        const functionToPlay = activated
            ? featureToSave.save({ organizationId: organization.pk, eventId: event?.id })
            : featureToSave.delete({ id: name, organizationId: organization.pk, eventId: event?.id }, { key: name });
        functionToPlay
            .then(() => {
                toast.success(t(`ticket.ticket_design.designs.premium.success_${activated ? 'enable' : 'disable'}`));
            })
            .catch(() => {
                toast.error(t(`ticket.ticket_design.designs.premium.error_${activated ? 'enable' : 'disable'}`));
                setToggleState(!activated);
            })
            .finally(() => {
                setLoadingToggle(false);
            });
    }, [isDisabled, toggleState, event, organization, loadingToggle]);

    function TooltipToggle({ ...props }) {
        return (
            <div className={Style['premium-title']}>
                {only_admin && <Chip primary icon="user" text={t('ticket.common.admin')} />}
                <Tooltip visible={isDisabled} margin={false} title={t('ticket.ticket_design.locked_customization')} arrow triggerOnHover>
                    <span className={Style['locked-toggle']} onClick={() => onSetFeatureFlag()}>
                        <Toggle {...props} checked={toggleState} />
                    </span>
                </Tooltip>
            </div>
        );
    }

    if (only_admin && !permissions.capacities.has('PyvarCapacity.ADMIN')) return null;

    return (
        <Card>
            <ToggleCard
                toggle={toggleState}
                disabled={isDisabled || loadingToggle}
                customToggle={TooltipToggle}
                title={t(`ticket.ticket_design.designs.premium.title${event ? '_event' : ''}`)}
                subtitle={t('ticket.ticket_design.designs.premium.subtitle')}
            ></ToggleCard>
        </Card>
    );
}

export default function DesignEvent({ organization, event = null, activeTab = 0, menhirUrl = '' }) {
    const { t } = useTranslation();
    const history = useHistory();
    const permissions = usePermissions();
    const isAdmin = React.useMemo(() => permissions.isAdmin(), [permissions]);

    const [designs, loadingDesigns] = DesignModel.useApiModel(
        {
            organizationId: organization.id,
            eventId: event.id,
            launch: Boolean(event.id && organization.id && !isAdmin),
            query: {
                only_default_event: true,
                limit: 1
            }
        },
        [event, organization, isAdmin]
    );

    const prefix = React.useMemo(() => {
        return `/ticket/O${organization?.id}/E${event?.id}/settings/tickets_design/designs`;
    }, [organization, event]);

    const panes = [
        {
            menuItem: t('ticket.ticket_design.designs.label'),
            menuIcon: 'image',
            customProp: '',
            render: () => {},
            url: ''
        }
    ];

    const [active, setActive] = React.useState(activeTab);

    React.useEffect(() => {
        setActive(activeTab);
    }, [activeTab]);

    if (!isAdmin && !loadingDesigns && !designs?.length) return <TicketDesignEmptyCard event={event} menhirUrl={menhirUrl} />;

    if (!isAdmin && loadingDesigns) return <Loading />;

    return (
        <>
            <WeezHeader
                title={t('ticket.ticket_design.title')}
                rightComponent={[
                    <Button key={0} onClick={() => history.push(`${prefix}/${active === 0 ? '' : 'rates/'}new`)} primary icon={<Icon name="plus" />}>
                        {t(`ticket.ticket_design.create_${active === 0 ? 'design' : ''}.title`)}{' '}
                    </Button>
                ]}
            >
                {t(`ticket.ticket_design.description${active === 0 && event ? '_event' : ''}`)}
            </WeezHeader>
            <SwitchFeature event={event} organization={organization} name={'EO_GABARIT'} />
            <Tab
                panes={panes}
                activeTab={active}
                onTabSelected={(tab, idx) => {
                    setActive(idx);
                    history.push(`${prefix}/${tab.url}`);
                }}
            >
                {active === 0 && <DesignList organization={organization} event={event} />}
            </Tab>
        </>
    );
}
